import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../../services/api';
import { resizeImageFile } from '../../utils/resizeImageFile';
import TrainingProgressPhotosHistory from './TrainingProgressPhotosHistory';

const DEFAULT_FIELDS = [
  { key: 'weight_kg', label: 'Peso (kg)' },
  { key: 'chest_cm', label: 'Pecho (cm)' },
  { key: 'shoulders_cm', label: 'Hombros (cm)' },
  { key: 'right_bicep_cm', label: 'Bícep derecho (cm)' },
  { key: 'left_bicep_cm', label: 'Bícep izquierdo (cm)' },
  { key: 'abdomen_above_cm', label: 'Abdomen +2 cm ombligo' },
  { key: 'abdomen_navel_cm', label: 'Abdomen ombligo' },
  { key: 'abdomen_below_cm', label: 'Abdomen -2 cm ombligo' },
  { key: 'glute_cm', label: 'Glúteo (cm)' },
  { key: 'right_thigh_cm', label: 'Muslo derecho (cm)' },
  { key: 'left_thigh_cm', label: 'Muslo izquierdo (cm)' },
  { key: 'right_calf_cm', label: 'Pantorrilla derecha (cm)' },
  { key: 'left_calf_cm', label: 'Pantorrilla izquierda (cm)' },
];

const FREQ_LABEL = {
  diaria: 'todos los días',
  cada_3_dias: 'cada 3 días',
  semanal: 'cada semana',
  quincenal: 'cada 15 días',
  mensual: 'cada mes',
};

const emptyForm = (fields) => ({
  recorded_at: new Date().toISOString().split('T')[0],
  notes: '',
  ...Object.fromEntries(fields.map((f) => [f.key, ''])),
});

export default function TrainingBodyMeasurements({ onBack = null, readOnlyUserId = null, embedded = false }) {
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [history, setHistory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoRules, setPhotoRules] = useState(null);
  const [form, setForm] = useState(emptyForm(DEFAULT_FIELDS));
  const [formPhotos, setFormPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const showFormRef = useRef(false);
  const coachView = readOnlyUserId != null;
  const photosApi = coachView
    ? `/training/coach/clients/${readOnlyUserId}/progress-photos`
    : '/training/progress-photos';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!coachView) {
        const cfgRes = await api.get('/training/measurements/field-config').catch(() => null);
        const cfgFields = cfgRes?.data?.data?.fields;
        if (Array.isArray(cfgFields) && cfgFields.length) {
          setFields(cfgFields);
          if (!showFormRef.current) setForm(emptyForm(cfgFields));
        }
        if (cfgRes?.data?.data?.photos) setPhotoRules(cfgRes.data.data.photos);
      }
      const measUrl = coachView
        ? `/training/coach/clients/${readOnlyUserId}/measurements`
        : '/training/measurements/me';
      const [res, photoRes] = await Promise.all([
        api.get(measUrl),
        api.get(photosApi),
      ]);
      setHistory(res.data?.data || []);
      setPhotos(photoRes.data?.data || []);
      if (photoRes.data?.rules) setPhotoRules(photoRes.data.rules);
    } catch {
      setError('No se pudieron cargar las medidas.');
    } finally {
      setLoading(false);
    }
  }, [coachView, readOnlyUserId, photosApi]);

  useEffect(() => {
    showFormRef.current = showForm;
  }, [showForm]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = useMemo(
    () => [...history]
      .reverse()
      .map((h) => ({
        fecha: h.recorded_at,
        peso: h.weight_kg,
        pecho: h.chest_cm,
        cintura: h.abdomen_navel_cm,
      })),
    [history],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (coachView) return;
    setSaving(true);
    setError('');
    try {
      const payload = { recorded_at: form.recorded_at, notes: form.notes };
      for (const f of fields) {
        if (form[f.key] !== '') payload[f.key] = Number(form[f.key]);
      }
      await api.post('/training/measurements', payload);
      const angles = Object.entries(formPhotos).filter(([, file]) => file);
      for (const [angle, file] of angles) {
        const prepared = await resizeImageFile(file);
        const fd = new FormData();
        fd.append('file', prepared);
        fd.append('angle', angle);
        fd.append('recorded_at', form.recorded_at);
        await api.post('/training/progress-photos', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setForm(emptyForm(fields));
      setFormPhotos({});
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (coachView || !window.confirm('¿Eliminar este registro?')) return;
    try {
      await api.delete(`/training/measurements/${id}`);
      await load();
    } catch {
      setError('No se pudo eliminar');
    }
  };

  return (
    <div className={`training-coach-view training-light-island surface-readable d28d-admin-surface ui-light-surface training-measures-panel ${embedded ? '' : 'max-w-4xl mx-auto'} p-4 rounded-xl border border-slate-200`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        {!embedded ? (
        <div>
          <h2 className="text-2xl font-bold text-stone-900">Medidas corporales</h2>
          <p className="text-sm text-stone-600">
            {coachView
              ? 'Historial de perímetros, peso y fotos del entrenado por fecha.'
              : 'Registra tu progreso; tu entrenador verá la evolución en seguimiento.'}
          </p>
        </div>
        ) : <div />}
        {onBack && (
          <button type="button" className="btn-secondary text-sm" onClick={onBack}>
            Volver
          </button>
        )}
      </div>

      {error && <p className="training-alert--error text-sm mb-3 text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>}

      {!coachView && (
        <button
          type="button"
          className="training-action-btn mb-4"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Cancelar' : '+ Nuevo registro'}
        </button>
      )}

      {showForm && !coachView && (
        <form onSubmit={handleSubmit} className="training-measures-form ui-light-surface surface-readable rounded-xl p-4 mb-6 grid sm:grid-cols-2 gap-3 border border-slate-200 bg-white">
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wide text-stone-700">Fecha</span>
            <input
              type="date"
              className="training-plan-field input mt-1 w-full"
              value={form.recorded_at}
              onChange={(e) => setForm({ ...form, recorded_at: e.target.value })}
              required
            />
          </label>
          {fields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-stone-700">{f.label}</span>
              <input
                type="number"
                step="0.1"
                className="training-plan-field input mt-1 w-full"
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wide text-stone-700">Notas</span>
            <textarea
              className="training-plan-field input mt-1 w-full"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          {(photoRules?.positions || []).length > 0 && (
            <div className="sm:col-span-2 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-stone-700">
                Fotos de esta toma (máx. {photoRules.max_photos})
              </p>
              <p className="text-xs text-stone-600">
                Tu coach pide {photoRules.max_photos} foto(s) {FREQ_LABEL[photoRules.frequency] || photoRules.frequency}.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {photoRules.positions.map((pos) => (
                  <label key={pos.key} className="block text-xs text-stone-700">
                    {pos.label}
                    <input
                      type="file"
                      accept="image/*"
                      className="mt-1 block w-full text-xs"
                      onChange={(e) => setFormPhotos((prev) => ({ ...prev, [pos.key]: e.target.files?.[0] || null }))}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
          <button type="submit" className="training-action-btn sm:col-span-2 w-full justify-center" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar medidas'}
          </button>
        </form>
      )}

      <section className="training-measures-photos ui-light-surface rounded-xl border border-slate-200 bg-white p-4 mb-6">
        <h3 className="font-bold text-stone-900 mb-1">Fotos de progreso</h3>
        <p className="text-sm text-stone-600 mb-4">
          {coachView
            ? 'Historial por fecha (frente, laterales y espalda). Las fotos quedan en el día en que se subieron.'
            : `Sube las fotos que pide tu coach${photoRules ? ` (${photoRules.max_photos} por toma, ${FREQ_LABEL[photoRules.frequency] || ''})` : ''}. Cada toma suma una fecha al historial.`}
        </p>
        {loading ? (
          <p className="text-sm text-stone-600">Cargando fotos…</p>
        ) : (
          <TrainingProgressPhotosHistory
            photos={photos}
            measurements={history}
            fields={fields}
            rules={photoRules}
            readOnly={coachView}
            apiBase={coachView ? photosApi : '/training/progress-photos'}
            mediaApiBase="/training/progress-photos"
            onChanged={load}
            onDeleteMeasurement={coachView ? null : handleDelete}
          />
        )}
      </section>

      {chartData.length > 1 && (
        <div className="surface-readable card p-4 mb-6 border border-slate-200 bg-white">
          <h3 className="font-bold text-stone-900 mb-3">Evolución</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} />
                <Tooltip contentStyle={{ color: '#0f172a', background: '#fff', border: '1px solid #e2e8f0' }} />
                <Line type="monotone" dataKey="peso" stroke="#84cc16" name="Peso kg" dot />
                <Line type="monotone" dataKey="cintura" stroke="#3b82f6" name="Cintura cm" dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
